// src/pages/SmartWeatherDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/SmartWeatherDashboard.css';
import { FaTemperatureHigh, FaTint, FaWind, FaCloudSun, FaThermometerHalf, FaFlask, FaWater, FaSmog, FaWifi, FaBolt, FaBatteryFull, FaLightbulb, FaTree } from 'react-icons/fa'; // Ikon sensor lebih banyak

// Fungsi pembantu untuk mendapatkan ikon sensor
const getSensorIcon = (sensorType) => {
    switch (sensorType) {
        case 'temperature_DHT11': return <FaTemperatureHigh className="sensor-icon" />; // Suhu udara
        case 'humidity_DHT11': return <FaTint className="sensor-icon" />; // Kelembaban udara
        case 'pressure_BMP180': return <FaWind className="sensor-icon" />; // Tekanan udara
        case 'soil_moisture': return <FaTree className="sensor-icon" />; // Kelembaban tanah
        case 'gas_mq': return <FaSmog className="sensor-icon" />; // Sensor gas
        case 'vibration_sensor': return <FaBolt className="sensor-icon" />; // Getaran/Akselerasi
        case 'temp_ds18b20': return <FaThermometerHalf className="sensor-icon" />; // Suhu air/lainnya
        case 'ph_sensor': return <FaFlask className="sensor-icon" />; // pH air
        case 'tds_sensor': return <FaWater className="sensor-icon" />; // TDS air
        case 'salinity_sensor': return <FaWater className="sensor-icon" />; // Kadar garam air
        case 'turbidity_sensor': return <FaSmog className="sensor-icon" />; // Kekeruhan air
        case 'voltage': return <FaBatteryFull className="sensor-icon" />; // Tegangan baterai
        case 'light_sensor': return <FaLightbulb className="sensor-icon" />; // Intensitas cahaya
        default: return <FaCloudSun className="sensor-icon" />; // Ikon default
    }
};

// Fungsi pembantu untuk mendapatkan unit dan nama tampilan sensor
const getSensorUnitAndDisplay = (sensorType, value) => {
    let unit = '';
    let displayValue = value;
    let label = '';
    switch (sensorType) {
        case 'temperature_DHT11':
        case 'temp_ds18b20':
            unit = '°C';
            label = 'Suhu';
            break;
        case 'humidity_DHT11':
        case 'soil_moisture':
            unit = '%';
            label = 'Kelembaban';
            break;
        case 'pressure_BMP180':
            unit = 'hPa'; // Hektopascal
            label = 'Tekanan';
            break;
        case 'gas_mq':
            unit = 'ppm'; // Parts per million
            label = 'Kualitas Udara (Gas)';
            break;
        case 'ph_sensor':
            unit = 'pH';
            label = 'pH Air';
            displayValue = value.toFixed(2); // Untuk pH biasanya 2 desimal
            break;
        case 'tds_sensor':
            unit = 'ppm';
            label = 'TDS Air';
            break;
        case 'salinity_sensor':
            unit = 'ppt'; // Parts per thousand atau mS/cm
            label = 'Kadar Garam';
            break;
        case 'turbidity_sensor':
            unit = 'NTU'; // Nephelometric Turbidity Unit
            label = 'Kekeruhan';
            break;
        case 'voltage':
            unit = 'V';
            label = 'Tegangan';
            break;
        case 'light_sensor':
            unit = 'lux';
            label = 'Cahaya';
            break;
        default:
            unit = '';
            label = 'Nilai';
            break;
    }
    return { label, displayValue: `${displayValue}${unit}`, rawValue: value };
};


const SmartWeatherDashboard = () => {
    const navigate = useNavigate();
    const [devices, setDevices] = useState([]);
    const [sensors, setSensors] = useState([]); // Konfigurasi sensor
    const [latestReadings, setLatestReadings] = useState({}); // ✅ State baru untuk pembacaan terbaru
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const token = localStorage.getItem('token');

    const fetchSmartIoTData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [devicesRes, sensorsRes, latestReadingsRes] = await Promise.all([ // ✅ Ambil juga latestReadings
                axios.get('https://depotaircerdas.com/api/superadmin/tables/smart_iot_devices', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('https://depotaircerdas.com/api/superadmin/tables/smart_iot_sensors', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('https://depotaircerdas.com/api/superadmin/tables/latest-sensor-readings', { headers: { Authorization: `Bearer ${token}` } }) // ✅ Endpoint baru
            ]);

            setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
            setSensors(Array.isArray(sensorsRes.data) ? sensorsRes.data : []);

            // ✅ Format latestReadings menjadi objek map untuk akses cepat
            const formattedReadings = {};
            if (Array.isArray(latestReadingsRes.data)) {
                latestReadingsRes.data.forEach(reading => {
                    formattedReadings[reading.sensor_id] = reading;
                });
            }
            setLatestReadings(formattedReadings);

        } catch (err) {
            console.error('Error fetching smart IoT data:', err);
            setError('Gagal memuat data Smart IoT. ' + (err.response?.data?.message || err.message));
            setDevices([]);
            setSensors([]);
            setLatestReadings({});
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }
        fetchSmartIoTData();

        // Optional: Auto-refresh data setiap X detik
        const refreshInterval = setInterval(fetchSmartIoTData, 30000); // Refresh setiap 30 detik
        return () => clearInterval(refreshInterval); // Cleanup interval
    }, [token, navigate, fetchSmartIoTData]);

    const getSensorsForDevice = (deviceId) => {
        return sensors.filter(sensor => sensor.device_id === deviceId);
    };

    const handleViewSensorDetail = (sensorId) => {
        navigate(`/admin/smart-weather/sensor/${sensorId}`);
    };

    if (loading) return <div className="smart-dashboard-loading">Memuat data Smart Weather...</div>;
    if (error) return <div className="smart-dashboard-error-message">{error}</div>;

    return (
        <div className="smart-weather-dashboard-container">
            <h2>Dashboard Smart Weather Monitoring</h2>
            <p className="intro-text">Pantau status perangkat dan sensor IoT Anda secara menyeluruh dan lihat pembacaan terkini dari setiap sensor.</p>

            <button onClick={fetchSmartIoTData} className="refresh-btn">Refresh Data</button>

            {devices.length === 0 ? (
                <p>Tidak ada perangkat IoT yang terdaftar.</p>
            ) : (
                <div className="devices-grid">
                    {devices.map(device => (
                        <div key={device.id} className="device-card">
                            <h3>{device.device_name} <FaWifi className="device-online-icon" /></h3>
                            <p><strong>Lokasi:</strong> {device.location || '-'}</p>
                            <p><strong>Tipe Perangkat:</strong> {device.device_type}</p>
                            <p><strong>Terakhir Online:</strong> {device.last_online_at ? new Date(device.last_online_at).toLocaleString() : '-'}</p>
                            <p><strong>Pemilik:</strong> {device.username}</p>

                            <h4>Sensor Terhubung:</h4>
                            <div className="sensor-readings-grid"> {/* ✅ Grid baru untuk kotak sensor */}
                                {getSensorsForDevice(device.id).length === 0 ? (
                                    <p className="no-sensor-message">Tidak ada sensor terhubung pada perangkat ini.</p>
                                ) : (
                                    getSensorsForDevice(device.id).map(sensor => {
                                        const latestReading = latestReadings[sensor.id];
                                        const { label, displayValue, rawValue } = getSensorUnitAndDisplay(sensor.sensor_type, latestReading ? latestReading.latest_value : 'N/A');
                                        
                                        // ✅ Tentukan status sensor (opsional: berdasarkan threshold min/max)
                                        let sensorStatusClass = 'status-normal';
                                        if (latestReading && typeof rawValue === 'number' && sensor.min_value !== null && sensor.max_value !== null) {
                                            if (rawValue < sensor.min_value || rawValue > sensor.max_value) {
                                                sensorStatusClass = 'status-alert'; // Di luar batas
                                            }
                                        } else if (!latestReading) {
                                            sensorStatusClass = 'status-inactive'; // Tidak ada pembacaan terbaru
                                        }

                                        return (
                                            <div key={sensor.id} className={`sensor-reading-box ${sensorStatusClass}`}>
                                                <div className="sensor-box-icon">
                                                    {getSensorIcon(sensor.sensor_type)}
                                                </div>
                                                <div className="sensor-box-info">
                                                    <p className="sensor-box-name">{sensor.sensor_name}</p>
                                                    <p className="sensor-box-label">{label}</p>
                                                    <p className="sensor-box-value">
                                                        <span>{displayValue}</span>
                                                    </p>
                                                    <p className="sensor-box-timestamp">
                                                        {latestReading ? new Date(latestReading.latest_timestamp).toLocaleTimeString() : 'N/A'}
                                                    </p>
                                                    <button onClick={() => handleViewSensorDetail(sensor.id)} className="view-detail-btn">Lihat Detail</button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SmartWeatherDashboard;